import { Collapse } from '@mui/material';
import { ReactNode } from 'react';
import { LimitedSxProps } from '@/utils';
import { Box } from '../Box';
import { useContainerContext, useIsContainerInModal } from './ContainerContextProvider';
import { useContainerLayout } from './useContainerLayout';

export interface ContainerBodyProps extends LimitedSxProps<'overflow' | 'overflowX' | 'overflowY'> {
  /**
   * Content of the container.
   */
  children: ReactNode;
  /**
   * By default the child component is unmounted after it reaches the
   * 'exited' state. Set `unmountOnExit` if you'd prefer to unmount the
   * component after it finishes exiting.
   */
  unmountOnExit?: boolean | undefined;
}

export const ContainerBody = ({
  children,
  unmountOnExit = true,
  // See sc-170049 for ticket to evaluate if we should change the default to 'hidden', which will require more due
  // diligence than letting people override the overflow on a case-by-case basis.
  overflow = 'auto',
  overflowX,
  overflowY,
}: ContainerBodyProps) => {
  const { collapsed, isLoading } = useContainerContext();
  const { isParentContainerInModal } = useIsContainerInModal();

  const containerLayout = useContainerLayout();

  const opacity = 1;

  return (
    <Collapse
      in={!collapsed || isParentContainerInModal}
      timeout="auto"
      unmountOnExit={unmountOnExit}
      sx={{
        flexGrow: 1,
        '& .MuiCollapse-wrapper': {
          height: '100%',
        },
        width: '100%',
      }}
      className="container-body"
    >
      <Box
        sx={{
          paddingY: `${containerLayout.body.paddingY}px`,
          paddingX: `${containerLayout.body.paddingX}px`,
          height: '100%',
          opacity,
          visibility: isLoading ? 'hidden' : 'visible', // hide container content when loading (initial load)
          overflow,
          overflowX,
          overflowY,
          // Hide padding when DataGrid is the only child
          [':has(> .MuiDataGrid-root:only-child)']: {
            paddingY: 0,
          },
        }}
        className="container-body-inner"
      >
        {children}
      </Box>
    </Collapse>
  );
};
